/** 手机触控：独立操作按钮使用至少44px点击区域；桌面保持原紧凑布局，业务权限、提交与跳转逻辑不变。 */
import { ResponsiveTable } from "../components/mobile/ResponsiveTable";
/** 手机记录列表复用原有数据、勾选与业务操作；普通记录按字段卡片呈现，交互表头和对比矩阵保留横向浏览。 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, ReferenceLine, Legend, LineChart, Line } from "recharts";
import * as XLSX from "xlsx";
import { STAGE_LABEL, STAGE_COLOR, regionLabel } from "../data/crm";
import { ForecastMonthDrill } from "../components/crm/ForecastMonthDrill";
import { Button } from "../components/ui/button";
import { SourceManager } from "../components/SourceManager";
import { ALL_MARKETING_DATES, MarketingPeriodFilter, marketingPeriodLabel } from "../components/crm/MarketingPeriodFilter";
import { ServiceTypeBoard } from "../components/ServiceTypeBoard";
import ProjectMap from "../components/ProjectMap";
import CrmHealthCard from "../components/CrmHealthCard";
import EvalScheduleBoard from "../components/EvalScheduleBoard";
import { TestDataCleanupDialog } from "../components/crm/TestDataCleanupDialog";
import { toast } from "../components/ui/sonner";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Users, TrendingUp, DollarSign, Flame, Target, PhoneCall, Trophy, Sparkles, AlertTriangle, Bot, Loader2, Gift, BellRing, MessageSquare, FileDown, Download, Star, Tags, Clock, Building2 } from "lucide-react";

/* KPI 卡：sub 为可选副标题，用于交代口径说明。
   副标题在 1024 窄卡下会超出单行宽度，故允许折行（leading-tight）而不是 truncate，避免口径被截掉看不见。 */
const Card = ({ icon: Icon, label, value, tint, sub, subTitle }) => (
  <div className="kpi-accent card-soft pl-5 pr-4 py-4 hover:shadow-lift transition-shadow">
    <div className="flex-wrap md:flex-nowrap flex items-center gap-2 text-slate-500 text-xs font-semibold tracking-wide"><span className="h-7 w-7 rounded-lg flex items-center justify-center" style={{ background: (tint || "#4F6BFF") + "18" }}><Icon className="h-4 w-4" style={{ color: tint }} /></span>{label}</div>
    <p className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-display tracking-tight mt-2 truncate">{value}</p>
    {sub && <p className="mt-1 text-[11px] leading-tight text-slate-400" title={subTitle || sub}>{sub}</p>}
  </div>
);

export default function CRMDashboard() {
  const { api, lang, user, featVis } = useApp();
  const zh = lang === "zh";
  const nav = useNavigate();
  useEffect(() => {
    if (user && user.role === "sales") nav("/crm/leads", { replace: true });
  }, [user, nav]);
  const [d, setD] = useState(null);
  const [dashboardFailed, setDashboardFailed] = useState(false);
  const [dashboardAttempt, setDashboardAttempt] = useState(0);
  const [mkt, setMkt] = useState([]);
  const [mktPeriod, setMktPeriod] = useState(ALL_MARKETING_DATES);
  const [mktLoading, setMktLoading] = useState(true);
  const [mktError, setMktError] = useState(false);
  const [mktRevision, setMktRevision] = useState(0);
  const [priority, setPriority] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [drillMonth, setDrillMonth] = useState(null);
  const [cleanup, setCleanup] = useState(false);
  const [churn, setChurn] = useState(null);
  const [aiLoading, setAiLoading] = useState(true);
  const [nudging, setNudging] = useState(false);
  const [refLb, setRefLb] = useState([]);
  const [roiTarget, setRoiTarget] = useState(0);
  const [quoteFunnel, setQuoteFunnel] = useState(null);
  const [funnelView, setFunnelView] = useState("total");
  const [stalled, setStalled] = useState(null);
  const [escalated, setEscalated] = useState(null);
  // 分公司级经理锁定本分公司：不可切换区域。
  // 【2026-10-22 修正】原来只认 sales_manager / marketing_manager 两个角色名。
  // 被管理员单独授权的分公司市场负责人（典型：圣地亚哥 Yvonne，branch_sales_manager）
  // 在这里被判成「无区域限制」，后果有三：① 页面渲染区域切换按钮，她能点 LA / 北加；
  // ② `/crm/dashboard` 请求不带 region，拿到的范围取决于后端；③「来源管理」按钮的
  // 判定复用了 `!!lockedRegion`，于是**按钮永远不渲染** —— 只改角色名单没用。
  // 现在与后端 `_mm_region` / `_regions_for` 同口径：有分公司且非 hq，并且
  // （角色名单命中 或 被授权看 CRM 看板且不属于总部跨区角色）。
  const HQ_WIDE_ROLES = ["admin", "ceo", "sales_vp", "marketing", "eng_manager"];
  const lockedRegion = (() => {
    if (!user?.region || user.region === "hq") return "";
    if (["sales_manager", "marketing_manager"].includes(user?.role)) return user.region;
    if (HQ_WIDE_ROLES.includes(user?.role)) return "";
    return featVis("/crm/dashboard", false) ? user.region : "";
  })();
  const [activeRegion, setActiveRegion] = useState(lockedRegion);
  // lockedRegion 依赖的菜单权限是异步加载的：首帧可能还是空串，等权限到了必须把
  // activeRegion 也跟过去，否则页面一直按「未锁定」渲染（下拉还在、数据请求不带 region）。
  useEffect(() => { if (lockedRegion) setActiveRegion(lockedRegion); }, [lockedRegion]);
  const [srcMgrRegion, setSrcMgrRegion] = useState(null);
  const [cmpMetric, setCmpMetric] = useState("roi"); // roi | spend
  const canSeeFunnelTrend = ["admin", "ceo", "sales_vp", "sales_manager"].includes(user?.role);
  const [funnelTrend, setFunnelTrend] = useState(null);
  const [snapBusy, setSnapBusy] = useState(false);
  const [dwellTop, setDwellTop] = useState(null);
  const openDwellTop = async () => {
    if (dwellTop) { setDwellTop(null); return; }
    try { const { data } = await api.get("/crm/funnel-health/dwell-top"); setDwellTop(data.stages || []); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "加载停留明细失败" : "Load failed")); }
  };
  useEffect(() => {
    if (canSeeFunnelTrend) api.get("/crm/funnel-health/trend").then(({ data }) => setFunnelTrend(data.items || [])).catch(() => setFunnelTrend([]));
  }, [api, canSeeFunnelTrend]);
  const genSnapshot = async () => {
    setSnapBusy(true);
    try {
      await api.post("/crm/funnel-health/run");
      const { data } = await api.get("/crm/funnel-health/trend");
      setFunnelTrend(data.items || []);
      toast.success(zh ? "已生成本周快照并推送周报给管理层" : "Snapshot generated & digest pushed");
    } catch (e) { toast.error(e?.response?.data?.detail || (zh ? "生成失败" : "Failed")); }
    setSnapBusy(false);
  };
  const funnelTrendData = useMemo(() => (funnelTrend || []).map((s) => {
    const vals = Object.values(s.dwell || {});
    const tot = vals.reduce((a, v) => a + (v.count || 0), 0);
    const avg = tot ? Math.round((vals.reduce((a, v) => a + (v.avgDays || 0) * (v.count || 0), 0) / tot) * 10) / 10 : null;
    return { day: (s.day || "").slice(5), regressions: s.regressions || 0, autoFixes: s.autoFixes || 0, restores: s.restores || 0, avgDwell: avg };
  }), [funnelTrend]);
  const { cmpData, cmpFlagged } = React.useMemo(() => {
    const bySource = {};
    (mkt || []).forEach((g) => (g.rows || []).forEach((r) => {
      const k = r.source;
      bySource[k] = bySource[k] || { source: k, _maxSpend: 0 };
      bySource[k][g.region] = cmpMetric === "roi" ? (r.roi ?? 0) : (r.adSpend ?? 0);
      bySource[k]._maxSpend = Math.max(bySource[k]._maxSpend, r.adSpend ?? 0);
    }));
    const all = Object.values(bySource);
    const flagged = [];
    let data = all;
    if (cmpMetric === "roi") {
      // 花费近乎为零的渠道 ROI 会爆表, 从对比图剔除并单独标注, 避免拉花坐标轴
      data = all.filter((d) => { if ((d._maxSpend || 0) < 50) { flagged.push(d.source); return false; } return true; });
    }
    data = data
      .sort((a, b) => ((b.la || 0) + (b.norcal || 0) + (b.sd || 0)) - ((a.la || 0) + (a.norcal || 0) + (a.sd || 0)))
      .slice(0, 8);
    return { cmpData: data, cmpFlagged: flagged };
  }, [mkt, cmpMetric]);
  const [digest, setDigest] = useState(null);
  const [digestOpen, setDigestOpen] = useState(false);
  const openDigest = async () => {
    setDigestOpen(true); setDigest(null);
    try { const { data } = await api.get("/crm/analytics/marketing/weekly-digest"); setDigest(data); }
    catch (e) { toast.error(e?.detailText || (zh ? "加载失败" : "Failed")); setDigestOpen(false); }
  };
  const downloadDigest = () => {
    if (!digest) return;
    const head = zh ? ["分公司", "总Leads", "总花费$", "签约数", "整体ROI%", "最佳渠道", "最佳ROI%", "最差渠道", "最差ROI%"] : ["Branch", "Leads", "Spend$", "Signed", "ROI%", "BestChannel", "BestROI%", "WorstChannel", "WorstROI%"];
    const rows = [head];
    (digest.branches || []).forEach((b) => rows.push([b.name, b.totalLeads, b.totalSpend, b.totalSigned, b.roi ?? "-", b.best?.source || "-", b.best?.roi ?? "-", b.worst?.source || "-", b.worst?.roi ?? "-"]));
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "digest");
    XLSX.writeFile(wb, `channel_digest_${digest.period}.xlsx`);
  };
  const [aiQ, setAiQ] = useState("");
  const [aiQBusy, setAiQBusy] = useState(false);
  const [aiQRes, setAiQRes] = useState(null);
  const runAiQuery = async () => {
    if (!aiQ.trim()) return;
    setAiQBusy(true);
    try { const { data } = await api.post("/crm/ai-query", { question: aiQ }); setAiQRes(data); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "AI 暂不可用" : "AI unavailable")); }
    setAiQBusy(false);
  };
  const [regions] = useState([{ id: "la", zh: "洛杉矶", en: "Los Angeles" }, { id: "norcal", zh: "北加州", en: "Northern California" }, { id: "sd", zh: "圣地亚哥", en: "San Diego" }]);

  /** 手机网络中断时提供可恢复的错误状态，避免看板永久停在加载中。
   * 重试只读取看板，不重复触发 AI 预测；切换区域后忽略旧请求，防止串区显示。
   */
  useEffect(() => {
    let current = true;
    setD(null);
    setDashboardFailed(false);
    const rq = activeRegion ? `?region=${encodeURIComponent(activeRegion)}` : "";
    api.get(`/crm/dashboard${rq}`).then(({ data }) => {
      if (current) setD(data);
    }).catch(() => {
      if (current) setDashboardFailed(true);
    });
    return () => { current = false; };
  }, [api, activeRegion, dashboardAttempt]);

  useEffect(() => {
    let current = true;
    const rq = activeRegion ? `?region=${encodeURIComponent(activeRegion)}` : "";
    setAiLoading(true);
    setQuoteFunnel(null);
    api.get(`/crm/priority${rq}`).then((r) => setPriority(r.data || [])).catch(() => {});
    api.get("/crm/referrals/leaderboard").then((r) => setRefLb(r.data || [])).catch(() => {});
    api.get(`/crm/quote-funnel${rq}`).then((r) => { if (current) setQuoteFunnel(r.data); }).catch(() => { if (current) setQuoteFunnel(null); });
    api.get("/crm/quote-stalled?days=3").then((r) => setStalled(r.data)).catch(() => {});
    api.get("/crm/quote-stalled?days=7").then((r) => setEscalated(r.data)).catch(() => {});
    Promise.allSettled([api.get(`/crm/ai/forecast${rq}`), api.get(`/crm/ai/churn${rq}`)]).then(([f, c]) => {
      if (!current) return;
      if (f.status === "fulfilled") setForecast(f.value.data); else setForecast(null);
      if (c.status === "fulfilled") setChurn(c.value.data); else setChurn(null);
      setAiLoading(false);
    });
    return () => { current = false; };
  }, [activeRegion]); // eslint-disable-line

  const [mktScope, setMktScope] = useState(() => { try { return localStorage.getItem("crm.mktScope") === "all" ? "all" : "assigned"; } catch { return "assigned"; } });
  const loadMkt = () => setMktRevision(value => value + 1);
  const pickMktScope = (s) => { setMktScope(s); try { localStorage.setItem("crm.mktScope", s); } catch {} };
  /** 日期/客户范围统一交给服务端统计。快速切换时废弃旧响应，失败时隐藏旧结果并允许原条件重试。 */
  useEffect(() => {
    let current = true;
    setMktLoading(true);
    setMktError(false);
    const params = { scope: mktScope, ...(mktPeriod.start ? { start: mktPeriod.start, end: mktPeriod.end } : {}) };
    api.get("/crm/analytics/marketing", { params }).then(({ data }) => {
      if (current) setMkt(Array.isArray(data) ? data : []);
    }).catch(() => { if (current) setMktError(true); })
      .finally(() => { if (current) setMktLoading(false); });
    return () => { current = false; };
  }, [api, mktScope, mktPeriod.start, mktPeriod.end, mktRevision]);
  useEffect(() => {
    api.get("/crm/settings").then((r) => setRoiTarget(r.data?.roiTarget || 0)).catch(() => {});
  }, [api]);

  const runNudge = async () => {
    setNudging(true);
    try { const { data } = await api.post("/crm/churn/nudge"); toast.success(zh ? `已提醒销售 ${data.nudgesSent} 条（高危 ${data.highRisk}）` : `Nudged ${data.nudgesSent}`); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "触发失败" : "Failed")); }
    setNudging(false);
  };

  const loadLeaderboard = () => api.get("/crm/referrals/leaderboard").then((r) => setRefLb(r.data || [])).catch(() => {});
  const payReward = async (key) => {
    try { const { data } = await api.post("/crm/referrals/pay-rewards", { referrerKey: key }); const message = zh ? `已登记 ${data.paidCount} 笔奖励，共 $${data.paidAmount.toLocaleString()}` : `Recorded ${data.paidCount}`; if (data.reviewCount) toast.warning(zh ? `${message}；另有 ${data.reviewCount} 笔可能重复，请先核对。` : `${message}; ${data.reviewCount} require duplicate review.`); else toast.success(message); await loadLeaderboard(); }
    catch (e) { toast.error(e?.response?.data?.detail || (zh ? "发放失败" : "Failed")); }
  };
  const sendWinback = async (r, tone = "friendly") => {
    const content = (r.suggestedMessages && r.suggestedMessages[tone]) || r.suggestedMessage;
    try { await api.post(`/crm/leads/${r.id}/nurture`, { channel: "SMS", content }); toast.success(zh ? `已向 ${r.clientName} 发送挽回话术（Mock）` : "Win-back sent (mock)"); }
    catch { toast.error(zh ? "发送失败" : "Failed"); }
  };
  const sendWinbackAI = async (r) => {
    const tid = toast.loading(zh ? "AI 正在为该客户撰写话术…" : "AI writing…");
    try { const { data } = await api.post(`/crm/leads/${r.id}/winback-ai`, { send: true, channel: "SMS" }); toast.dismiss(tid); toast.success((zh ? `已发送 AI 话术给 ${r.clientName}（Mock）：\n` : "AI win-back sent: ") + data.message, { duration: 8000 }); }
    catch (e) { toast.dismiss(tid); toast.error(e?.response?.data?.detail || (zh ? "AI 生成失败" : "Failed")); }
  };
  const exportForecastExcel = () => {
    if (!forecast) return;
    const rows = (forecast.byStage || []).map((s) => ({
      [zh ? "阶段" : "Stage"]: (zh ? STAGE_LABEL[s.stage]?.zh : STAGE_LABEL[s.stage]?.en) || s.stage,
      [zh ? "客户数" : "Leads"]: s.count, [zh ? "在途总额" : "Raw"]: s.raw, [zh ? "加权额" : "Weighted"]: s.weighted,
    }));
    rows.push({});
    rows.push({ [zh ? "阶段" : "Stage"]: zh ? "加权在途预测" : "Weighted pipeline", [zh ? "加权额" : "Weighted"]: forecast.weightedPipeline });
    rows.push({ [zh ? "阶段" : "Stage"]: zh ? "月度签约目标" : "Monthly quota", [zh ? "加权额" : "Weighted"]: forecast.monthlyQuota });
    rows.push({ [zh ? "阶段" : "Stage"]: zh ? "预测 vs 目标缺口" : "Gap", [zh ? "加权额" : "Weighted"]: (forecast.weightedPipeline - forecast.monthlyQuota) });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, zh ? "销售预测" : "Forecast");
    XLSX.writeFile(wb, `sales_forecast_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const exportForecastPdf = async () => {
    try {
      const res = await api.get(`/crm/ai/forecast/pdf?lang=${zh ? "zh" : "en"}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = "sales_forecast.pdf"; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error(zh ? "导出失败" : "Export failed"); }
  };
  const exportMarketing = (group) => {
    // 导出使用同一次已完成筛选的返回值，附带日期与估算说明，避免表格离开页面后失去统计口径。
    if (mktLoading || mktError) return;
    const rows = (group.rows || []).map((m) => ({
      [zh ? "来源" : "Source"]: m.source, Leads: m.leads, [zh ? "有效" : "Qualified"]: m.qualified,
      [zh ? "签约" : "Signed"]: m.signed, [zh ? "广告花费" : "Spend"]: m.adSpend, CPL: m.cpl,
      [zh ? "获客成本" : "Cost/Signed"]: m.costPerSigned, [zh ? "转化率%" : "Conv%"]: m.conversion, "ROI%": m.roi,
      [zh ? "费用口径" : "Spend basis"]: m.spendMode === "actual" ? (zh ? "已登记" : "Recorded") : (zh ? "含估算" : "Includes estimates"),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, (zh ? group.name : group.nameEn).slice(0, 28));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      [zh ? "统计区间（含起止当天）" : "Date range (inclusive)", marketingPeriodLabel(mktPeriod, zh)],
      [zh ? "客户范围" : "Lead scope", mktScope === "all" ? (zh ? "全部客户（含公海）" : "All leads, including pool") : (zh ? "仅已分配销售" : "Assigned only")],
      [zh ? "时间口径" : "Date basis", zh ? "客户录入日期（加州时区）；有效、签约为当前状态" : "Lead creation date (Los Angeles); current qualified/signed status"],
      [zh ? "费用口径" : "Spend basis", zh ? "整月采用已登记花费；部分月份按天折算；缺失月份按单条成本估算" : "Recorded monthly spend; partial months prorated; missing months estimated per lead"],
    ]), zh ? "筛选说明" : "Filter details");
    XLSX.writeFile(wb, `marketing_${group.region}_${mktPeriod.start ? `${mktPeriod.start}_${mktPeriod.end}` : "all"}.xlsx`);
  };

  if (dashboardFailed) return (
    <div role="alert" className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-8 space-y-3" data-testid="crm-dashboard-error">
      <h1 className="text-lg font-semibold text-slate-900">{zh ? "CRM 看板暂时无法加载" : "CRM dashboard could not load"}</h1>
      <p className="text-sm text-slate-600">{zh ? "请检查网络后重新加载。" : "Check your connection and try again."}</p>
      <Button className="min-h-11 w-full sm:w-auto" onClick={() => setDashboardAttempt(value => value + 1)}>{zh ? "重新加载" : "Retry"}</Button>
    </div>
  );
  if (!d) return <div role="status" className="p-8 text-slate-500">{zh ? "加载中…" : "Loading…"}</div>;
  const k = d.kpis;
  const isMgr = ["admin", "ceo", "sales_vp", "sales_manager", "marketing"].includes(d.role);
  // 分公司经理由服务端按 region 锁定名单；该视图需要显示分公司全部在职销售（含 0 业绩），总部看板仍保留前 8 名。
  const branchSalesRanking = d.rankingScope === "branch";
  const canViewSalesRanking = isMgr || branchSalesRanking;
  const rankingRows = branchSalesRanking ? (d.ranking || []) : (d.ranking || []).slice(0, 8);
  /**
   * 「营销来源效果分析」板块可见性。
   *
   * 需求（2026-10-21，圣地亚哥区域市场部经理 Yvonne）：她要看到这块来源效果看板，
   * 且**只显示她所在区域（SD）**。她实际角色是 branch_sales_manager，不在下面的
   * 硬编码名单里，所以之前整块板块对她不存在；后端 /crm/analytics/marketing 也 403。
   *
   * 为什么改成读模块授权而不是往名单里加角色名：
   *   往名单加 "branch_sales_manager" 会让**所有**分公司销售部经理都看到营销花费与 ROI
   *   ——那是市场部的经营数据，不是销售经理的职责范围。走 featVis（= 管理员在
   *   「用户管理 → 菜单权限」里对该账号的显式授权），才是「按人授权、不动全体」。
   *   未授权账号走角色默认值，行为与改造前逐字一致。
   *
   * 为什么判定 key 用 /crm/dashboard 而不是 /crm/analytics/marketing：
   *   后者不是权限目录里的模块 key（MODULE_CATALOG 里没有），featVis 对它恒为 false，
   *   写了也不会生效。本板块只出现在 CRM 看板页内，能进看板 = 有 /crm/dashboard 授权，
   *   用它做判定与「页面级准入」语义一致，也避免凭空新增一个无人维护的权限项。
   *   区域收敛由后端 _regions_for 负责（授权账号只拿到本分公司一组）：
   *   前端这里决定「能不能看」，后端决定「看多少」。
   */
  const canSeeMarketing = isMgr || d.role === "marketing_manager" || featVis("/crm/dashboard", false);
  /**
   * 「来源管理」按钮的可见性。
   *
   * 【2026-10-23 改造】改为**直接用服务端下发的判定**（/crm/dashboard 返回的 canManageSources），
   * 不再在前端拼角色名单。原因：来源管理的准入有三层判断（岗位身份 / 分公司锁定 / 账号级授权），
   * 前端复制一份必然漂移 —— 昨天就出过一次「前端用 isMgr（比后端宽），CEO 看见按钮却 403」，
   * 反向还漏了「有按钮但没权限」的账号（昨天 Yvonne 的按钮根本不渲染）。
   * 服务端是唯一事实来源：前端只负责「能不能点」，范围由服务端 sourcesRegion 一并下发。
   *
   * sourcesRegion 语义：非空 = 被锁定到某个分公司（只能管这家）；null / undefined = 不锁
   * （总部角色可跨公司管理）。锁定时来源管理弹窗只显示本分公司，不给切换。
   */
  const canManageSources = !!d.canManageSources;
  const sourcesRegion = d.sourcesRegion || "";
  // 初次加载和请求失败时也保留日期入口；已加载分公司的标题稳定，切换时间段不会把控件卸载。
  const marketingGroups = (mkt.length ? mkt : regions.map(r => ({ region: r.id, name: r.zh, nameEn: r.en, rows: [] })))
    .filter(g => !activeRegion || g.region === activeRegion);
  const funnel = (d.funnel || []).filter((f) => !["lost", "nurturing", "mm_review"].includes(f.stage));

  return (
    <div className="space-y-5" data-testid="crm-dashboard">
      {/* CRM 看板按需求隐藏阶段夜审和预留金提醒的只读预演，不挂载组件，避免占位和触发预演请求。
          后台定时任务照常运行；发布前核对页仍保留夜审预演，便于管理人员核查。 */}
      {isMgr && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="crm-ai-query">
          <div className="flex-wrap md:flex-nowrap flex items-center gap-1.5 mb-2"><Bot className="h-4 w-4 text-indigo-600" /><span className="text-sm font-semibold text-slate-800">{zh ? "AI 智能查询" : "Ask AI"}</span></div>
          <div className="flex-wrap md:flex-nowrap flex gap-2">
            <input data-testid="ai-query-input" value={aiQ} onChange={(e) => setAiQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runAiQuery()}
              placeholder={zh ? "用中文提问，如：给我看LA区停滞超7天、报价超20万的单" : "Ask in plain language…"}
              className="flex-1 text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300" />
            <Button data-testid="ai-query-btn" onClick={runAiQuery} disabled={aiQBusy || !aiQ.trim()} className="bg-indigo-600 text-white hover:bg-indigo-700">
              {aiQBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (zh ? "查询" : "Ask")}
            </Button>
          </div>
          {aiQRes && (
            <div className="mt-3 bg-slate-50 rounded-lg p-3" data-testid="ai-query-results">
              <p className="text-[12px] text-slate-500 mb-2">{aiQRes.summary} · {zh ? `共 ${aiQRes.count} 条` : `${aiQRes.count} results`}</p>
              {aiQRes.count === 0 ? <p className="text-sm text-slate-400 text-center py-2">{zh ? "没有匹配的线索" : "No matches"}</p> : (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {aiQRes.items.map((it) => (
                    <div key={it.leadId} data-testid={`ai-query-row-${it.leadId}`} onClick={() => nav(`/crm/leads/${it.leadId}`)}
                      className="flex-wrap md:flex-nowrap flex items-center gap-3 rounded-lg border border-slate-100 hover:border-indigo-300 px-3 py-2 cursor-pointer">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-slate-800 truncate">{it.clientName} <span className="text-[11px] text-slate-400 font-normal">· {it.assignedSales} · {(zh ? STAGE_LABEL[it.stage]?.zh : STAGE_LABEL[it.stage]?.en) || it.stage}</span></p>
                      </div>
                      {it.amount ? <span className="text-[12px] text-slate-600 whitespace-nowrap">${(it.amount / 1000).toFixed(0)}k</span> : null}
                      {it.daysSinceView != null ? <span className="text-[11px] text-amber-600 whitespace-nowrap">{zh ? `停滞${it.daysSinceView}天` : `${it.daysSinceView}d`}</span> : null}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{zh ? "CRM 看板" : "CRM Dashboard"} <span className="text-sm font-normal text-slate-400">· {d.role}</span></h1>
        {isMgr && lockedRegion && (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5" data-testid="crm-region-locked" data-region={lockedRegion}>
            <Building2 className="h-4 w-4" />{zh ? `所属分公司：${regions.find((x) => x.id === lockedRegion)?.zh || lockedRegion}` : `Branch: ${regions.find((x) => x.id === lockedRegion)?.en || lockedRegion}`}
          </span>
        )}
        {isMgr && !lockedRegion && (
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="crm-region-switcher">
            {[{ id: "", zh: "全部", en: "All" }, ...regions].map((r) => (
              <button key={r.id || "all"} data-testid={`region-tab-${r.id || "all"}`} onClick={() => setActiveRegion(r.id)}
                className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeRegion === r.id ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`)}>
                {zh ? r.zh : r.en}
              </button>
            ))}
          </div>
        )}
      </div>
      {isMgr && activeRegion && <p className="-mt-2 text-xs text-indigo-500" data-testid="crm-region-note">{zh ? `当前仅显示「${regions.find((x) => x.id === activeRegion)?.zh}」公司数据（独立运营）` : "Showing one company"}</p>}

      {isMgr && <CrmHealthCard api={api} region={activeRegion} zh={zh} />}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card icon={Users} label={zh ? "总客户（已分配销售）" : "Total Leads (assigned)"} value={k.totalLeads} tint="#6366f1" />
        <Card icon={TrendingUp} label={zh ? "本月新增" : "New (mo)"} value={k.newThisMonth} tint="#0ea5e9" />
        <Card icon={Target} label={zh ? "有效客户" : "Qualified"} value={k.qualified} tint="#8b5cf6" />
        <Card icon={DollarSign} label={zh ? "已签约" : "Signed"} value={k.signed} tint="#16a34a" />
        <Card icon={Flame} label={zh ? "高意向" : "Hot"} value={k.hot} tint="#dc2626" />
        <Card icon={PhoneCall} label={zh ? "今日待跟进" : "Due today"} value={k.dueToday} tint="#f97316" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {/* 【2026-10-29 口径修正】这一格原为「在途金额」，读 Σ 预计签约额，从来不可信：
            该字段只在签约后写入、签约又已排除在途之外，加上新建线索表单压根没有「预计签约额」
            输入框，于是结构性恒为 0（生产 460 条在途仅 1 条有值）。
            现在改为显示「在途客户数」——直接回答「手上还有多少没签的客户」，
            和右侧「已签约 163 条 / 转化率 / 流失率」同为真实计数，四格互相对得上（可加总回总客户）。
            金额预测不再放这里：已报价的金额只覆盖少数客户，全部估算又会凭空造出上亿，
            两种情况都比计数更容易误导；需要看金额请看下方「AI 销售预测」面板（那里有完整方法说明）。 */}
        <Card icon={Users} label={zh ? "在途客户" : "In pipeline"}
          value={k.pipelineActiveLeads ?? 0}
          tint="#f59e0b"
          sub={k.pipelineQuotedLeads > 0 ? (zh
            ? `其中 ${k.pipelineQuotedLeads} 个已报价`
            : `${k.pipelineQuotedLeads} quoted`) : null}
          subTitle={zh
            ? "尚未签约、且未流失 / 未判无效 / 未进复核池的客户数量（含培育中）。"
            : "Leads not yet signed, excluding lost/invalid/under-review (nurturing included)."} />
        <Card icon={DollarSign} label={zh ? "签约金额" : "Signed $"} value={`$${(k.signedValue / 1000000).toFixed(2)}M`} tint="#16a34a" />
        <Card icon={TrendingUp} label={zh ? "转化率" : "Conversion"} value={`${k.conversionRate}%`} tint="#6366f1" />
        <Card icon={TrendingUp} label={zh ? "流失率" : "Lost rate"} value={`${k.lostRate}%`} tint="#dc2626" />
      </div>

      {isMgr && <ServiceTypeBoard region={activeRegion} regions={regions} />}

      {/* 按看板阅读顺序，将现场评估日程紧邻报价转化漏斗上方；保留管理角色权限，报价为空时日程仍独立展示。 */}
      {isMgr && <EvalScheduleBoard zh={zh} region={activeRegion} regionLabel={regions.find((x) => x.id === activeRegion)?.[zh ? "zh" : "en"]} />}

      {quoteFunnel && quoteFunnel.sent.count > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="crm-quote-funnel">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex-wrap md:flex-nowrap flex items-center gap-2"><FileDown className="h-4 w-4 text-red-500" /><span className="text-sm font-semibold text-slate-800">{zh ? "报价转化漏斗" : "Quote Conversion Funnel"}</span></div>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="funnel-view-switcher">
              {[["total", "总览", "Overview"], ["sales", "按销售", "By Sales"], ["region", "按区域", "By Region"]].map(([id, z, e]) => (
                <button key={id} data-testid={`funnel-view-${id}`} onClick={() => setFunnelView(id)}
                  className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2.5 py-1 text-[12px] font-medium rounded-md transition-colors ${funnelView === id ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500 hover:text-slate-800"}`)}>
                  {zh ? z : e}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[["已发送", "Sent", quoteFunnel.sent, "#6366f1"], ["已浏览", "Viewed", quoteFunnel.viewed, "#f59e0b"], ["已接受", "Accepted", quoteFunnel.accepted, "#16a34a"]].map((s, i) => (
              <div key={i} data-testid={`funnel-${s[1].toLowerCase()}`} className="rounded-xl border border-slate-100 p-3 text-center" style={{ background: `${s[3]}0d` }}>
                <p className="text-[11px] text-slate-400">{zh ? s[0] : s[1]}</p>
                <p className="text-2xl font-bold" style={{ color: s[3] }}>{s[2].count}</p>
                <p className="text-[11px] text-slate-500">${(s[2].amount / 1000000).toFixed(2)}M</p>
              </div>
            ))}
          </div>
          <div className="flex-wrap md:flex-nowrap flex justify-around mt-3 text-[12px] text-slate-500">
            <span>{zh ? "浏览率" : "View rate"} <b className="text-amber-600">{quoteFunnel.viewRate}%</b></span>
            <span>{zh ? "接受率" : "Accept rate"} <b className="text-emerald-600">{quoteFunnel.acceptRate}%</b></span>
          </div>
          {funnelView !== "total" && (
            <div className="mt-4 overflow-x-auto" data-testid={`funnel-breakdown-${funnelView}`}>
              <ResponsiveTable className="w-full text-[12px]">
                <thead className="text-slate-400"><tr className="border-b border-slate-100">
                  <th className="text-left font-medium py-2">{funnelView === "sales" ? (zh ? "销售员" : "Salesperson") : (zh ? "区域" : "Region")}</th>
                  <th className="text-right font-medium py-2">{zh ? "已发送" : "Sent"}</th>
                  <th className="text-right font-medium py-2">{zh ? "已浏览" : "Viewed"}</th>
                  <th className="text-right font-medium py-2">{zh ? "已接受" : "Accepted"}</th>
                  <th className="text-right font-medium py-2">{zh ? "接受率" : "Accept%"}</th>
                </tr></thead>
                <tbody>
                  {(funnelView === "sales" ? quoteFunnel.bySales : quoteFunnel.byRegion).map((r) => (
                    <tr key={r.key} data-testid={`funnel-row-${r.key}`} className="border-b border-slate-50 hover:bg-slate-50/60">
                      <td className="py-2 font-medium text-slate-700">{funnelView === "region" ? regionLabel(regions, r.key, zh) : r.name}</td>
                      <td className="py-2 text-right text-slate-600">{r.sent.count} <span className="text-slate-300">·</span> ${(r.sent.amount / 1000000).toFixed(2)}M</td>
                      <td className="py-2 text-right text-amber-600">{r.viewed.count}</td>
                      <td className="py-2 text-right text-emerald-600">{r.accepted.count}</td>
                      <td className="py-2 text-right font-semibold text-slate-800">{r.acceptRate}%</td>
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>
            </div>
          )}
        </div>
      )}

      {escalated && escalated.count > 0 && (
        <div className="bg-white rounded-xl border-2 border-red-200 p-4" data-testid="crm-escalated-board">
          <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-red-500" />
            <span className="text-sm font-semibold text-slate-800">{zh ? "停滞升级看板" : "Escalated Quotes"}</span>
            <span className="text-[11px] bg-red-100 text-red-700 rounded-full px-2 py-0.5">{zh ? `已停滞 ≥7 天 · 需经理介入 · ${escalated.count}` : `Idle 7+ days · needs manager · ${escalated.count}`}</span>
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {escalated.items.map((s, i) => (
              <div key={s.leadId} data-testid={`escalated-row-${s.leadId}`} onClick={() => nav(`/crm/leads/${s.leadId}`)}
                className="flex-wrap md:flex-nowrap flex items-center gap-3 rounded-lg border border-brand-100 bg-brand-50/40 hover:border-brand-300 px-3 py-2 cursor-pointer transition-colors">
                <span className="text-[11px] font-bold text-red-400 w-5">#{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{s.clientName} <span className="text-[11px] text-slate-400 font-normal">· {s.assignedSales || (zh ? "未分配" : "Unassigned")}</span></p>
                  <p className="text-[11px] text-slate-400 truncate">{s.address}</p>
                </div>
                <span className="text-[12px] text-slate-500 whitespace-nowrap">${(s.amount / 1000).toFixed(0)}k</span>
                <span className="text-[11px] font-bold text-red-600 whitespace-nowrap">{zh ? `停滞 ${s.daysSinceView} 天` : `${s.daysSinceView}d idle`}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {stalled && stalled.count > 0 && (
        <div className="bg-white rounded-xl border border-amber-200 p-4" data-testid="crm-quote-stalled">
          <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-semibold text-slate-800">{zh ? "报价停滞清单" : "Stalled Quotes"}</span>
            <span className="text-[11px] bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{zh ? `已浏览超 3 天未接受 · ${stalled.count}` : `Viewed 3+ days ago, not accepted · ${stalled.count}`}</span>
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {stalled.items.map((s) => (
              <div key={s.leadId} data-testid={`stalled-row-${s.leadId}`} onClick={() => nav(`/crm/leads/${s.leadId}`)}
                className="flex-wrap md:flex-nowrap flex items-center gap-3 rounded-lg border border-slate-100 hover:border-amber-300 hover:bg-amber-50/40 px-3 py-2 cursor-pointer transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{s.clientName} <span className="text-[11px] text-slate-400 font-normal">· {s.assignedSales || (zh ? "未分配" : "Unassigned")}</span></p>
                  <p className="text-[11px] text-slate-400 truncate">{s.address}</p>
                </div>
                <span className="text-[12px] text-slate-500 whitespace-nowrap">${(s.amount / 1000).toFixed(0)}k</span>
                <span className="text-[11px] font-semibold text-amber-600 whitespace-nowrap">{zh ? `停滞 ${s.daysSinceView} 天` : `${s.daysSinceView}d idle`}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Funnel */}
        <div className={`bg-white rounded-xl border border-slate-200 p-4 ${lockedRegion ? "lg:col-span-3" : "lg:col-span-2"}`}>
          <p className="text-sm font-semibold text-slate-800 mb-3">{zh ? "销售漏斗" : "Sales Funnel"}</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={funnel} layout="vertical" margin={{ left: 30 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="stage" width={70} tick={{ fontSize: 11 }} tickFormatter={(s) => (zh ? STAGE_LABEL[s]?.zh : STAGE_LABEL[s]?.en) || s} />
              <Tooltip formatter={(v) => [v, zh ? "客户数" : "Leads"]} labelFormatter={(s) => (zh ? STAGE_LABEL[s]?.zh : STAGE_LABEL[s]?.en) || s} />
              <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                {funnel.map((e) => <Cell key={e.stage} fill={STAGE_COLOR[e.stage]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        {/* Region pie（分公司经理不显示） */}
        {!lockedRegion && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="crm-region-pie">
          <p className="text-sm font-semibold text-slate-800 mb-3">{zh ? "区域分布" : "By Region"}</p>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={d.byRegion} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${zh ? e.name : e.region}: ${e.count}`}>
                {d.byRegion.map((e, i) => <Cell key={i} fill={["#4F6BFF", "#f59e0b", "#16a34a"][i % 3]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
        )}
      </div>

      {/* Priority + Ranking */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="crm-priority">
          <p className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-indigo-600" />{zh ? "今日优先联系" : "Today's Priority"}</p>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {priority.map((p, i) => (
              <div key={p.id} onClick={() => nav(`/crm/leads/${p.id}`)} data-testid={`priority-${p.id}`} className="flex-wrap md:flex-nowrap flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                <span className="text-xs font-bold text-white bg-indigo-600 rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">{i + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 truncate">{p.clientName} <span className="text-[11px] text-slate-400">· {p.assignedSales || (zh ? "未分配" : "Unassigned")}</span></p>
                  <p className="text-[11px] text-orange-600 truncate">{p.reason}</p>
                </div>
                <span className="text-xs font-bold text-slate-400">{p.priorityScore}</span>
              </div>
            ))}
            {priority.length === 0 && <p className="text-sm text-slate-300 text-center py-4">—</p>}
          </div>
        </div>
        {canViewSalesRanking && (
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" />{zh ? "销售业绩排名" : "Sales Ranking"}</p>
            <ResponsiveTable className="w-full text-sm">
              <thead className="text-xs text-slate-400"><tr><th className="text-left font-medium py-1">{zh ? "销售" : "Sales"}</th><th className="text-right font-medium">{zh ? "客户" : "Leads"}</th><th className="text-right font-medium">{zh ? "签约" : "Signed"}</th><th className="text-right font-medium">{zh ? "金额" : "Value"}</th></tr></thead>
              <tbody>
                {rankingRows.map((r) => (
                  <tr key={r.sales} className="border-t border-slate-50"><td className="py-1.5 text-slate-700">{r.sales}</td><td className="text-right text-slate-500">{r.leads}</td><td className="text-right text-green-600 font-semibold">{r.signed}</td><td className="text-right text-slate-700">${(r.value / 1000).toFixed(0)}k</td></tr>
                ))}
              </tbody>
            </ResponsiveTable>
            {(d.orphanSales || []).length > 0 && ["admin", "ceo", "sales_vp"].includes(d.role) && (
              <button type="button" onClick={() => setCleanup(true)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 mt-2 w-full text-left text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 hover:bg-amber-100 flex items-center gap-1.5" data-testid="crm-ranking-orphan-notice" data-count={d.orphanSales.reduce((a, o) => a + o.leads, 0)}>
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />{zh ? `已隐藏 ${d.orphanSales.length} 个非在职 / 演示销售（${d.orphanSales.map((o) => `${o.sales} ${o.leads}`).join("、")}）· 点击清理测试数据` : `${d.orphanSales.length} inactive/demo sales hidden · click to clean test data`}
              </button>
            )}
            {cleanup && <TestDataCleanupDialog api={api} zh={zh} onClose={() => setCleanup(false)} onDone={() => api.get(`/crm/dashboard${activeRegion ? `?region=${activeRegion}` : ""}`).then((r) => setD(r.data)).catch(() => {})} />}
          </div>
        )}
      </div>

      {/* AI Forecast + Churn */}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="crm-ai-insights">
        <div className="bg-gradient-to-br from-indigo-50 to-white rounded-xl border border-indigo-100 p-4 min-w-0 overflow-hidden" data-testid="crm-forecast">
          <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Bot className="h-4 w-4 text-indigo-600" />{zh ? "AI 销售预测" : "AI Sales Forecast"}</p>
            {isMgr && forecast && (
              <div className="flex-wrap md:flex-nowrap flex items-center gap-1.5">
                <Button data-testid="forecast-export-excel" size="sm" variant="outline" onClick={exportForecastExcel} className="h-7 text-xs"><Download className="h-3.5 w-3.5 mr-1" />Excel</Button>
                <Button data-testid="forecast-export-pdf" size="sm" variant="outline" onClick={exportForecastPdf} className="h-7 text-xs"><FileDown className="h-3.5 w-3.5 mr-1" />PDF</Button>
              </div>
            )}
          </div>
          {aiLoading && !forecast ? <div className="flex-wrap md:flex-nowrap flex items-center gap-2 text-sm text-slate-400 py-6"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "AI 分析中…" : "Analyzing…"}</div> : forecast ? (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-white rounded-lg p-2 border border-slate-100"><p className="text-[11px] text-slate-400">{zh ? "加权在途" : "Weighted"}</p><p className="text-lg font-bold text-indigo-600">${(forecast.weightedPipeline / 1000000).toFixed(2)}M</p></div>
                <div className="bg-white rounded-lg p-2 border border-slate-100"><p className="text-[11px] text-slate-400">{zh ? "在途总额" : "Raw"}</p><p className="text-lg font-bold text-slate-700">${(forecast.rawPipeline / 1000000).toFixed(2)}M</p></div>
                <div className="bg-white rounded-lg p-2 border border-slate-100"><p className="text-[11px] text-slate-400">{zh ? "活跃客户" : "Active"}</p><p className="text-lg font-bold text-slate-700">{forecast.activeLeads}</p></div>
              </div>
              <p className="text-[10px] text-slate-500 mb-2 flex flex-wrap gap-x-2" data-testid="crm-forecast-method">
                <span>{zh ? "口径：客户线索 · Leads（已分配，新线索→合同）" : "Scope: assigned leads, new→contract"}</span>
                {forecast.estimatedAmountLeads > 0 && <span data-testid="crm-forecast-estimated">· {zh ? `${forecast.estimatedAmountLeads} 条金额按历史签约中位数 $${Math.round((forecast.calibration?.medianAmount || 0) / 1000)}k 估算` : `${forecast.estimatedAmountLeads} amounts estimated at median $${Math.round((forecast.calibration?.medianAmount || 0) / 1000)}k`}</span>}
                {forecast.inferredMonthLeads > 0 && <span data-testid="crm-forecast-inferred">· {zh ? `${forecast.inferredMonthLeads} 条月份按阶段推算` : `${forecast.inferredMonthLeads} months inferred`}</span>}
                {(forecast.probabilities || []).length > 0 && <span data-testid="crm-forecast-probs">· {zh ? "概率（近 12 个月）" : "Probability (12 mo)"} {forecast.probabilities.map((p) => `${zh ? (STAGE_LABEL[p.stage]?.zh || p.stage) : (STAGE_LABEL[p.stage]?.en || p.stage)} ${Math.round(p.prob * 100)}%`).join(" · ")}</span>}
              </p>
              {(forecast.byMonth || []).length > 0 && (
                <ResponsiveContainer width="100%" height={130}>
                  <BarChart data={forecast.byMonth} style={{ cursor: "pointer" }}>
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis hide />
                    <Tooltip formatter={(v) => [`$${(v / 1000).toFixed(0)}k`, zh ? "加权额" : "Weighted"]} labelFormatter={(l) => `${l} · ${zh ? "点击查看明细" : "click for details"}`} />
                    {forecast.monthlyQuota > 0 && <ReferenceLine y={forecast.monthlyQuota} stroke="#dc2626" strokeDasharray="4 3" label={{ value: `${zh ? "月目标" : "Quota"} $${(forecast.monthlyQuota / 1000).toFixed(0)}k`, fontSize: 10, fill: "#dc2626", position: "insideTopRight" }} />}
                    <Bar dataKey="weighted" fill="#4F6BFF" radius={[4, 4, 0, 0]} onClick={(d) => { const mo = d?.payload?.month || d?.month; if (mo) setDrillMonth((cur) => (cur === mo ? null : mo)); }}>
                      {forecast.byMonth.map((m) => <Cell key={m.month} fill={drillMonth && drillMonth !== m.month ? "#4F6BFF55" : "#4F6BFF"} data-testid={`crm-forecast-bar-${m.month}`} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
              {(forecast.byMonth || []).length > 0 && (
                <div className="flex flex-wrap items-center gap-1 mt-0.5" data-testid="crm-forecast-months">
                  <span className="text-[10px] text-slate-400" data-testid="crm-forecast-drill-hint">{zh ? "点击月份查看线索明细：" : "Click a month for lead details:"}</span>
                  {forecast.byMonth.map((m) => (
                    <button key={m.month} type="button" onClick={() => setDrillMonth((cur) => (cur === m.month ? null : m.month))} data-testid={`crm-forecast-month-${m.month}`} data-on={drillMonth === m.month ? 1 : 0}
                      className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`text-[10px] rounded-full border px-2 py-0.5 transition-colors ${drillMonth === m.month ? "bg-indigo-600 text-white border-indigo-600" : "bg-white/70 text-slate-600 border-slate-200 hover:border-indigo-300"}`)}>
                      {m.month} · {m.count}
                    </button>
                  ))}
                </div>
              )}
              <ForecastMonthDrill forecast={forecast} month={drillMonth} zh={zh} onClose={() => setDrillMonth(null)} onOpenLead={(id) => nav(`/crm/leads/${id}`)} />
              {forecast.monthlyQuota > 0 && <p className="text-[11px] text-slate-400 mt-1" data-testid="crm-forecast-quota">{zh ? "红色虚线为月度签约目标" : "Red dashed line = monthly sign quota"} · ${(forecast.monthlyQuota / 1000).toFixed(0)}k</p>}
              {forecast.narrative && <div className="mt-2 text-xs text-slate-600 whitespace-pre-wrap bg-white/70 rounded-lg p-3 border border-indigo-50 max-h-44 overflow-y-auto" data-testid="crm-forecast-ai">{forecast.narrative}</div>}
            </>
          ) : <p className="text-sm text-slate-300 py-6 text-center">—</p>}
        </div>

        <div className="bg-gradient-to-br from-red-50 to-white rounded-xl border border-red-100 p-4 min-w-0 overflow-hidden" data-testid="crm-churn">
          <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><AlertTriangle className="h-4 w-4 text-red-500" />{zh ? "AI 客户流失预警" : "AI Churn Risk"}</p>
            {isMgr && <Button data-testid="crm-churn-nudge" size="sm" variant="outline" onClick={runNudge} disabled={nudging} className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50">{nudging ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><BellRing className="h-3.5 w-3.5 mr-1" />{zh ? "一键提醒销售" : "Nudge sales"}</>}</Button>}
          </div>
          {aiLoading && !churn ? <div className="flex-wrap md:flex-nowrap flex items-center gap-2 text-sm text-slate-400 py-6"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "AI 分析中…" : "Analyzing…"}</div> : churn ? (
            <>
              <p className="text-[10px] text-slate-500 mb-2" data-testid="crm-churn-method">{zh ? `口径：客户线索 · Leads（已分配，新线索→合同）· 已扫描 ${churn.summary.scanned ?? "—"} 位客户，公海 / 已签约 / 流失不计` : `Scope: assigned leads, new→contract · ${churn.summary.scanned ?? "—"} scanned (pool / signed / lost excluded)`}</p>
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="bg-white rounded-lg p-2 border border-slate-100 text-center"><p className="text-[11px] text-slate-400">{zh ? "高危" : "High"}</p><p className="text-lg font-bold text-red-600">{churn.summary.high}</p></div>
                <div className="bg-white rounded-lg p-2 border border-slate-100 text-center"><p className="text-[11px] text-slate-400">{zh ? "中危" : "Med"}</p><p className="text-lg font-bold text-orange-500">{churn.summary.medium}</p></div>
                <div className="bg-white rounded-lg p-2 border border-slate-100 text-center"><p className="text-[11px] text-slate-400">{zh ? "低危" : "Low"}</p><p className="text-lg font-bold text-amber-500">{churn.summary.low}</p></div>
                <div className="bg-white rounded-lg p-2 border border-slate-100 text-center"><p className="text-[11px] text-slate-400">{zh ? "风险额" : "At risk"}</p><p className="text-sm font-bold text-slate-700">${(churn.summary.valueAtRisk / 1000).toFixed(0)}k</p></div>
              </div>
              <div className="space-y-1 max-h-36 overflow-y-auto mb-2">
                {(churn.atRisk || []).slice(0, 6).map((r) => (
                  <div key={r.id} data-testid={`churn-${r.id}`} className="flex-wrap md:flex-nowrap flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/70">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${r.level === "high" ? "bg-red-500" : r.level === "medium" ? "bg-orange-400" : "bg-amber-400"}`} />
                    <div className="min-w-0 flex-1 cursor-pointer" onClick={() => nav(`/crm/leads/${r.id}`)}><p className="text-xs font-medium text-slate-700 truncate">{r.clientName} <span className="text-[10px] text-slate-400">· {r.assignedSales || "-"}</span></p><p className="text-[10px] text-red-500 truncate">{r.reasons.join(" · ")}</p></div>
                    <span className="text-xs font-bold text-slate-400">{r.riskScore}</span>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`churn-winback-${r.id}`} title={zh ? "选择话术一键发送" : "Win-back message"} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 flex-shrink-0 text-slate-400 hover:text-indigo-600 p-1 rounded-md hover:bg-indigo-50"><MessageSquare className="h-3.5 w-3.5" /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem data-testid={`winback-ai-${r.id}`} onClick={() => sendWinbackAI(r)}><Sparkles className="h-3.5 w-3.5 mr-1.5 text-indigo-500" />{zh ? "AI 智能生成" : "AI-written"}</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`winback-friendly-${r.id}`} onClick={() => sendWinback(r, "friendly")}>💬 {zh ? "温和亲切" : "Friendly"}</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`winback-urgent-${r.id}`} onClick={() => sendWinback(r, "urgent")}>⏰ {zh ? "紧迫催单" : "Urgent"}</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`winback-offer-${r.id}`} onClick={() => sendWinback(r, "offer")}>🎁 {zh ? "优惠促成" : "Offer"}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
                {churn.atRisk.length === 0 && <p className="text-xs text-slate-300 text-center py-3">{zh ? "暂无高风险客户" : "No at-risk leads"}</p>}
              </div>
              {churn.narrative && <div className="text-xs text-slate-600 whitespace-pre-wrap bg-white/70 rounded-lg p-3 border border-red-50 max-h-40 overflow-y-auto" data-testid="crm-churn-ai">{churn.narrative}</div>}
            </>
          ) : <p className="text-sm text-slate-300 py-6 text-center">—</p>}
        </div>
      </div>

      {/* Referral rewards leaderboard */}
      {isMgr && refLb.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="crm-referral-leaderboard">
          <p className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-1.5"><Gift className="h-4 w-4 text-emerald-500" />{zh ? "转介绍激励排行" : "Referral Rewards Leaderboard"}</p>
          <div className="overflow-x-auto">
            <ResponsiveTable className="w-full text-sm">
              <thead className="text-xs text-slate-400 bg-slate-50/60"><tr>
                {(zh ? ["推荐客户", "负责销售", "推荐数", "已转化", "签约额", "已发奖励", "待发奖励", ""] : ["Referrer", "Sales", "Referrals", "Converted", "Signed $", "Rewards Paid", "Pending", ""]).map((h, i) => <th key={i} className={`font-medium px-3 py-2 ${i > 1 && i < 7 ? "text-right" : "text-left"}`}>{h}</th>)}
              </tr></thead>
              <tbody>
                {refLb.map((r, i) => (
                  <tr key={i} className={`border-t border-slate-50 ${r.spotlight ? "bg-amber-50/60" : ""}`} data-testid={`ref-lb-${i}`}>
                    <td className="px-3 py-2 text-slate-700 flex items-center gap-1.5">{i < 3 && <span className={`w-4 h-4 rounded-full text-[10px] font-bold text-white flex items-center justify-center ${["bg-amber-400", "bg-slate-400", "bg-orange-400"][i]}`}>{i + 1}</span>}{r.referrerName}{r.spotlight && <span data-testid={`ref-spotlight-${i}`} title={zh ? "签约自动发放奖励" : "Auto-rewarded"} className="inline-flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full"><Star className="h-3 w-3 fill-amber-500 text-amber-500" />{zh ? "明星推荐人" : "Star"}</span>}</td>
                    <td className="px-3 py-2 text-slate-500">{r.referrerSales || "-"}</td>
                    <td className="px-3 py-2 text-right">{r.referrals}</td>
                    <td className="px-3 py-2 text-right text-green-600 font-semibold">{r.converted}</td>
                    <td className="px-3 py-2 text-right text-slate-700">${(r.signedValue / 1000).toFixed(0)}k</td>
                    <td className="px-3 py-2 text-right text-emerald-600">${r.rewardsPaid.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right text-amber-600">${(r.rewardsPending || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">{r.rewardsPending > 0 && <Button data-testid={`ref-pay-${i}`} size="sm" onClick={() => payReward(r.referrerKey)} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"><DollarSign className="h-3.5 w-3.5 mr-0.5" />{zh ? "发放" : "Pay"}</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
        </div>
      )}

      {/* Cross-branch source comparison — spend / ROI across the 3 companies */}
      {isMgr && !mktLoading && !mktError && (mkt || []).length > 1 && cmpData.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="marketing-branch-compare">
          <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <Target className="h-4 w-4 text-indigo-500" />
              {zh ? "三家分公司来源对比" : "Source Comparison Across Branches"}
              <span className="text-[11px] font-normal text-slate-400">{zh ? "(哪家哪个渠道最划算)" : "(which branch & channel wins)"}</span>
            </p>
            <div className="inline-flex items-center gap-2">
              <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden text-xs">
                <button data-testid="cmp-metric-roi" onClick={() => setCmpMetric("roi")} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-3 py-1 ${cmpMetric === "roi" ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-50"}`)}>ROI%</button>
                <button data-testid="cmp-metric-spend" onClick={() => setCmpMetric("spend")} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-3 py-1 ${cmpMetric === "spend" ? "bg-indigo-600 text-white" : "text-slate-500 hover:bg-slate-50"}`)}>{zh ? "广告花费" : "Spend"}</button>
              </div>
              {canManageSources && <Button data-testid="mkt-weekly-digest-btn" size="sm" variant="outline" onClick={openDigest} className="h-7 text-xs"><FileDown className="h-3.5 w-3.5 mr-1" />{zh ? "渠道周报" : "Digest"}</Button>}
            </div>
          </div>
          <p className="mb-2 text-xs text-slate-500">{zh ? "统计区间：" : "Date range: "}{marketingPeriodLabel(mktPeriod, zh)}</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={cmpData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
              <XAxis dataKey="source" tick={{ fontSize: 11 }} interval={0} angle={-12} textAnchor="end" height={48} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => cmpMetric === "roi" ? `${v}%` : `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => cmpMetric === "roi" ? `${v}%` : `$${Number(v).toLocaleString()}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="la" name={zh ? "洛杉矶" : "LA"} fill="#4F6BFF" radius={[3, 3, 0, 0]} />
              <Bar dataKey="norcal" name={zh ? "北加州" : "NorCal"} fill="#f59e0b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="sd" name={zh ? "圣地亚哥" : "SD"} fill="#16a34a" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {cmpMetric === "roi" && cmpFlagged.length > 0 && (
            <p className="mt-2 text-[11px] text-amber-600 flex items-center gap-1" data-testid="cmp-flagged-note">
              <AlertTriangle className="h-3.5 w-3.5" />
              {zh ? `以下渠道花费近乎为零、ROI 失真，已从对比图剔除：${cmpFlagged.join("、")}` : `Excluded near-zero-spend channels (ROI distorted): ${cmpFlagged.join(", ")}`}
            </p>
          )}
        </div>
      )}

      {canSeeFunnelTrend && funnelTrend !== null && (
        <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="funnel-health-trend">
          <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-2">
            <div>
              <p className="text-sm font-semibold text-slate-800">{zh ? "漏斗健康趋势" : "Funnel Health Trend"}</p>
              <p className="text-[11px] text-slate-400">{zh ? "每周一自动存档：回退/自动修正/还原次数 + 全漏斗平均停留天数（右轴）" : "Weekly snapshots: regressions / auto-fixes / restores + avg dwell days (right axis)"}</p>
            </div>
            <div className="flex-wrap md:flex-nowrap flex items-center gap-2">
              <Button data-testid="funnel-dwell-btn" size="sm" variant="outline" onClick={openDwellTop} className="h-7 text-xs">
                <Clock className="h-3.5 w-3.5 mr-1" />{dwellTop ? (zh ? "收起明细" : "Hide") : (zh ? "停留明细" : "Dwell detail")}
              </Button>
              <Button data-testid="funnel-snapshot-btn" size="sm" variant="outline" onClick={genSnapshot} disabled={snapBusy} className="h-7 text-xs">
                {snapBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <TrendingUp className="h-3.5 w-3.5 mr-1" />}{zh ? "生成本周快照" : "Snapshot now"}
              </Button>
            </div>
          </div>
          {funnelTrendData.length === 0 ? (
            <p className="text-sm text-slate-300 text-center py-6" data-testid="funnel-trend-empty">{zh ? "暂无历史快照 — 点击「生成本周快照」开始记录，每周一系统也会自动存档" : "No snapshots yet — click Snapshot now"}</p>
          ) : (
            <div onClick={openDwellTop} className="cursor-pointer" title={zh ? "点击图表查看各阶段停留最久的客户" : "Click to view longest-dwelling leads"}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={funnelTrendData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <YAxis yAxisId="l" tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => `${v}天`} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="l" type="monotone" dataKey="regressions" name={zh ? "回退" : "Regressions"} stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} />
                <Line yAxisId="l" type="monotone" dataKey="autoFixes" name={zh ? "自动修正" : "Auto-fixes"} stroke="#4F6BFF" strokeWidth={2} dot={{ r: 3 }} />
                <Line yAxisId="l" type="monotone" dataKey="restores" name={zh ? "还原" : "Restores"} stroke="#16a34a" strokeWidth={2} dot={{ r: 3 }} />
                <Line yAxisId="r" type="monotone" dataKey="avgDwell" name={zh ? "平均停留(天)" : "Avg dwell (d)"} stroke="#f59e0b" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} connectNulls />
              </LineChart>
            </ResponsiveContainer>
            </div>
          )}
          {dwellTop && (
            <div className="mt-3 grid md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="funnel-dwell-panel">
              {dwellTop.length === 0 && <p className="text-xs text-slate-300 col-span-full text-center py-3">{zh ? "暂无活跃客户" : "No active leads"}</p>}
              {dwellTop.map((g) => (
                <div key={g.stage} className="rounded-lg border border-slate-100 p-2.5" data-testid={`dwell-stage-${g.stage}`}>
                  <p className="text-[11px] font-bold text-slate-500 mb-1">{zh ? g.label : g.stage} <span className="font-normal text-slate-300">Top {g.leads.length}</span></p>
                  {g.leads.map((l) => (
                    <button key={l.id} data-testid={`dwell-lead-${l.id}`} onClick={() => nav(`/crm/leads/${l.id}`)}
                      className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full flex items-center justify-between text-xs py-1 border-b border-slate-50 hover:text-indigo-600 text-left">
                      <span className="truncate mr-1">{l.clientName || l.id}<span className="ml-1 text-[10px] text-slate-300">{l.assignedSales || ""}</span></span>
                      <span className={`shrink-0 font-bold ${l.days >= 30 ? "text-red-600" : l.days >= 14 ? "text-amber-600" : "text-slate-500"}`}>{l.days}{zh ? "天" : "d"}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Marketing analytics — one independent table per company (region) */}
      {canSeeMarketing && marketingGroups.map((group) => (
        <div key={group.region} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`crm-marketing-${group.region}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="text-sm font-semibold text-slate-800 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: { la: "#6366f1", norcal: "#f59e0b", sd: "#16a34a" }[group.region] }} />
              {zh ? `营销来源效果分析 · ${group.name}` : `Marketing Source Analytics · ${group.nameEn}`}
              <span className="text-[11px] font-normal text-slate-400">({zh ? "独立核算" : "independent"})</span>
              {roiTarget > 0 && <span className="text-[11px] font-normal text-red-500">· {zh ? `ROI 预警线 ${roiTarget}%` : `alert < ${roiTarget}%`}</span>}
            </p>
            <div className="flex-wrap md:flex-nowrap flex items-center gap-2">
              <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-[11px]" data-testid={`mkt-scope-toggle-${group.region}`} data-scope={mktScope} title={zh ? "仅已分配销售：与客户线索页 / CRM 看板同口径；全部客户：含公海未分配客户（营销获客口径）" : "Assigned only matches Leads page; All includes pool leads"}>
                <button type="button" data-testid={`mkt-scope-assigned-${group.region}`} onClick={() => pickMktScope("assigned")} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2 py-1 ${mktScope === "assigned" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`)}>{zh ? "仅已分配销售" : "Assigned only"}</button>
                <button type="button" data-testid={`mkt-scope-all-${group.region}`} onClick={() => pickMktScope("all")} className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2 py-1 border-l border-slate-200 ${mktScope === "all" ? "bg-indigo-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`)}>{zh ? "全部客户（含公海）" : "All leads"}</button>
              </div>
              {/* 【2026-10-23】锁定本分公司时（sourcesRegion 非空），只在**自己公司**的卡片上
                  显示来源管理入口：分公司销售部经理管的是自家来源，别家公司的卡片上没有她的入口。
                  总部角色（sourcesRegion 为空）仍在每张卡片上显示，可跨公司管理。 */}
              {canManageSources && (!sourcesRegion || sourcesRegion === group.region) && <Button data-testid={`marketing-manage-sources-${group.region}`} size="sm" variant="outline" onClick={() => setSrcMgrRegion(group.region)} className="h-7 text-xs"><Tags className="h-3.5 w-3.5 mr-1" />{zh ? "来源管理" : "Sources"}</Button>}
              <Button data-testid={`marketing-export-${group.region}`} size="sm" variant="outline" disabled={mktLoading || mktError} onClick={() => exportMarketing(group)} className="h-7 text-xs"><Download className="h-3.5 w-3.5 mr-1" />Excel</Button>
            </div>
          </div>
          <MarketingPeriodFilter value={mktPeriod} onChange={setMktPeriod} zh={zh} id={`marketing-period-${group.region}`} />
          {mktLoading ? <p role="status" className="flex items-center gap-2 py-8 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />{zh ? "正在更新所选时间段的数据…" : "Updating selected date range…"}</p> :
            mktError ? <div role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">
              {zh ? "此时间段的数据加载失败，请重试。" : "Could not load this date range. Please retry."}
              <Button variant="outline" className="ml-2 min-h-11" onClick={loadMkt}>{zh ? "重试" : "Retry"}</Button>
            </div> : <>
          <p className="mb-3 text-xs leading-relaxed text-slate-500">{zh ? "花费：活动/广告费用按发生日期逐笔计入（同月有明细时以明细为准）；未逐笔登记时整月使用已登记金额，部分月份按天折算，缺失月份按单条成本估算。标为“含估算”的花费及相关 ROI、获客成本仅供参考。" : "Spend: campaign expenses counted by their own date (detail wins over monthly totals); otherwise recorded monthly totals, prorated for partial months, per-lead cost when missing. Estimated costs and related ROI are indicative."}</p>
          {group.totals.spendNoLeads > 0 && (
            <p className="mb-3 text-xs text-amber-700" data-testid={`marketing-spend-nolead-${group.region}`}>
              {zh ? `本月已花费 $${Number(group.totals.adSpend).toLocaleString()}，其中 $${Number(group.totals.spendNoLeads).toLocaleString()} 来自尚未产生客户的渠道（活动可能还没开始）。` : `Spent $${Number(group.totals.adSpend).toLocaleString()}, of which $${Number(group.totals.spendNoLeads).toLocaleString()} has no leads yet.`}
            </p>
          )}
          {group.missingDateLeads > 0 && <p className="mb-3 text-xs text-amber-700">{zh ? `${group.missingDateLeads} 条旧客户缺少有效录入日期，未计入当前时间段；切换“全部时间”可查看。` : `${group.missingDateLeads} leads have no valid creation date and are excluded; use All time to include them.`}</p>}
          <SourceCharts group={group} zh={zh} roiTarget={roiTarget} />
          {group.excludedLeads > 0 && (
            <p className="text-[11px] text-slate-400 px-1 -mt-1 mb-2" data-testid={`marketing-excluded-${group.region}`} title={zh ? "这些来源是历史 / 系统写入的线索来源值，不在「来源管理」类别里，图表与合计均未计入；如需统计请在来源管理中添加同名类别" : "Lead source values not defined in Source Management are excluded from the chart and totals; add them in Source Management to include"}>
              {zh ? `另有 ${group.excludedLeads} 条线索的来源不在类别管理中（${group.excluded.map((e) => `${e.source} ×${e.leads}`).join("、")}），未计入` : `${group.excludedLeads} leads with sources not in Source Management are excluded (${group.excluded.map((e) => `${e.source} ×${e.leads}`).join(", ")})`}
              {canManageSources && <span className="ml-1 text-indigo-500">{zh ? "· 点右上「来源管理」补上同名类别即会计入" : " · add them in Sources"}</span>}
            </p>
          )}
          <div className="overflow-x-auto">
            <ResponsiveTable className="w-full text-sm">
              <thead className="text-xs text-slate-400 bg-slate-50/60"><tr>
                {["来源", "Leads", "有效", "签约", "广告花费", "CPL", "获客成本", "转化率", "ROI"].map((h, i) => <th key={i} className={`font-medium px-3 py-2 ${i ? "text-right" : "text-left"}`}>{zh ? h : ["Source", "Leads", "Qualified", "Signed", "Spend", "CPL", "Cost/Signed", "Conv%", "ROI%"][i]}</th>)}
              </tr></thead>
              <tbody>
                {group.rows.map((m) => {
                  const lowRoi = roiTarget > 0 && m.roi != null && m.roi < roiTarget;
                  return (
                  <tr key={m.source} className={`border-t border-slate-50 ${lowRoi ? "bg-red-50/60" : ""}`} data-testid={lowRoi ? `roi-alert-${group.region}-${m.source}` : undefined}>
                    <td className="px-3 py-2 text-slate-700">{m.source}{lowRoi && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-600">{zh ? "低于预警" : "low"}</span>}{!(m.leads > 0) && m.adSpend > 0 && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" title={zh ? "已登记花费，但还没有客户来自这个渠道" : "Spend recorded, no leads yet"}>{zh ? "仅花费" : "spend only"}</span>}</td>
                    <td className="px-3 py-2 text-right">{m.leads}</td>
                    <td className="px-3 py-2 text-right">{m.qualified}</td>
                    <td className="px-3 py-2 text-right text-green-600 font-semibold">{m.signed}</td>
                    <td className="px-3 py-2 text-right">${m.adSpend.toLocaleString()}<span className={`ml-1 text-[10px] ${m.spendMode === "actual" ? "text-emerald-700" : "text-amber-700"}`}>{m.spendMode === "actual" ? (zh ? "已登记" : "Recorded") : (zh ? "含估算" : "Estimated")}</span></td>
                    <td className="px-3 py-2 text-right">${m.cpl}</td>
                    <td className="px-3 py-2 text-right">{m.costPerSigned != null ? `$${m.costPerSigned}` : "-"}</td>
                    <td className="px-3 py-2 text-right">{m.conversion}%</td>
                    <td className={`px-3 py-2 text-right font-semibold ${m.roi > 0 ? "text-green-600" : m.roi != null ? "text-red-500" : "text-slate-400"}`}>{m.roi != null ? `${m.roi}%` : "-"}</td>
                  </tr>
                  );
                })}
                {group.rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-300 py-4">{zh ? "暂无数据" : "No data"}</td></tr>}
              </tbody>
              {group.rows.length > 0 && (
                <tfoot><tr className="border-t-2 border-slate-100 font-semibold text-slate-700 bg-slate-50/40">
                  <td className="px-3 py-2">{zh ? "合计" : "Total"}</td>
                  <td className="px-3 py-2 text-right">{group.totals.leads}</td>
                  <td className="px-3 py-2 text-right">{group.totals.qualified}</td>
                  <td className="px-3 py-2 text-right text-green-600">{group.totals.signed}</td>
                  <td className="px-3 py-2 text-right">${group.totals.adSpend.toLocaleString()}</td>
                  <td className="px-3 py-2" colSpan={2}></td>
                  <td className="px-3 py-2 text-right">{group.totals.conversion}%</td>
                  <td className={`px-3 py-2 text-right ${group.totals.roi > 0 ? "text-green-600" : "text-red-500"}`}>{group.totals.roi != null ? `${group.totals.roi}%` : "-"}</td>
                </tr></tfoot>
              )}
            </ResponsiveTable>
          </div>
          </>}
        </div>
      ))}
      {/* ROI 预警线是全公司唯一一条设置，后端 PUT /crm/settings 只放 admin / ceo
          （与 admin_role.is_admin 同口径）。没写权限的人只读，别让 blur 打出 403。
          【2026-10-23】lockRegion 改用服务端下发的 sourcesRegion：
          分公司销售部经理的来源管理锁定到自家分公司，与后端写入时的区域校验一致。 */}
      <SourceManager open={srcMgrRegion !== null} initialRegion={srcMgrRegion} lockRegion={sourcesRegion || undefined} onClose={() => setSrcMgrRegion(null)} api={api} zh={zh} regions={regions} onChanged={() => loadMkt()} canEditSettings={["admin", "ceo"].includes(user?.role)} />
      <Dialog open={digestOpen} onOpenChange={(v) => !v && setDigestOpen(false)}>
        <DialogContent className="max-w-2xl" data-testid="mkt-digest-modal">
          <DialogHeader>
            <DialogTitle>{zh ? "渠道效果周报" : "Weekly Channel Digest"}{digest ? ` · ${digest.period}` : ""}</DialogTitle>
            <DialogDescription>{zh ? "每家分公司本月最佳/最差渠道（花费≥$50 才参与评优，避免极小花费 ROI 失真）。可下载 Excel，站内每周一自动推送。" : "Best/worst channel per branch (spend ≥ $50). Download Excel; auto-pushed weekly."}</DialogDescription>
          </DialogHeader>
          {!digest ? (
            <div className="flex-wrap md:flex-nowrap py-10 flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" />{zh ? "生成中…" : "Loading…"}</div>
          ) : (
            <div className="space-y-3">
              {(digest.branches || []).map((b) => (
                <div key={b.region} className="border border-slate-100 rounded-lg p-3" data-testid={`digest-branch-${b.region}`}>
                  <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-1.5">
                    <p className="text-sm font-semibold text-slate-800">{b.name}</p>
                    <span className="text-[11px] text-slate-400">{zh ? "Leads" : "Leads"} {b.totalLeads} · {zh ? "花费" : "Spend"} ${Number(b.totalSpend || 0).toLocaleString()} · {zh ? "签约" : "Signed"} {b.totalSigned} · ROI {b.roi != null ? `${b.roi}%` : "-"}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-emerald-50 rounded-md px-2.5 py-1.5"><span className="text-emerald-700 font-medium">{zh ? "最佳渠道" : "Best"}</span>: {b.best ? `${b.best.source} · ROI ${b.best.roi}% ($${Number(b.best.adSpend).toLocaleString()})` : (zh ? "暂无有效渠道" : "n/a")}</div>
                    <div className="bg-red-50 rounded-md px-2.5 py-1.5"><span className="text-red-600 font-medium">{zh ? "最差渠道" : "Worst"}</span>: {b.worst ? `${b.worst.source} · ROI ${b.worst.roi}% ($${Number(b.worst.adSpend).toLocaleString()})` : (zh ? "暂无有效渠道" : "n/a")}</div>
                  </div>
                </div>
              ))}
              <div className="flex justify-end">
                <Button data-testid="digest-download" size="sm" onClick={downloadDigest} className="bg-indigo-600 hover:bg-indigo-700 text-white h-8"><Download className="h-3.5 w-3.5 mr-1" />{zh ? "下载 Excel" : "Download Excel"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {["sales_vp", "sales_manager", "marketing_manager", "admin", "ceo"].includes(user.role) && (
        <div data-testid="crm-vp-project-map">
          <ProjectMap />
        </div>
      )}
    </div>
  );
}

function SourceCharts({ group, zh, roiTarget }) {
  const [view, setView] = useState("funnel");
  /**
   * 【2026-10-22】原来这里只留 `leads > 0` 的渠道，导致「广告费花了、客户还没来」的渠道
   * 在整张图里彻底消失 —— 圣地亚哥 Yvonne 的原话是「一定有一段时间是花了钱没客户的广告，
   * 那不是花了钱但是显示不出来了吗」。有花费就保留，让钱看得见。
   */
  const data = useMemo(() => {
    const rows = (group.rows || []).filter((r) => (r.leads || 0) > 0 || (r.adSpend || 0) > 0);
    if (view === "roi") return rows.filter((r) => r.roi != null).sort((a, b) => b.roi - a.roi);
    if (view === "cost") return rows.filter((r) => r.costPerSigned != null).sort((a, b) => a.costPerSigned - b.costPerSigned);
    return [...rows].sort((a, b) => (b.leads || 0) - (a.leads || 0) || (b.adSpend || 0) - (a.adSpend || 0));
  }, [group.rows, view]);
  const noLead = useMemo(() => (group.rows || []).filter((r) => !(r.leads > 0) && (r.adSpend || 0) > 0), [group.rows]);
  if (!(group.rows || []).some((r) => (r.leads || 0) > 0 || (r.adSpend || 0) > 0)) return null;
  const btn = (key, label) => (
    <button data-testid={`src-chart-${key}-${group.region}`} onClick={() => setView(key)}
      className={"min-h-11 min-w-11 md:min-h-0 md:min-w-0 " + (`px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors ${view === key ? "bg-white text-indigo-600 shadow-sm" : "text-slate-500"}`)}>{label}</button>
  );
  return (
    <div className="mb-3 rounded-lg bg-slate-50/60 border border-slate-100 p-3" data-testid={`src-charts-${group.region}`}>
      <div className="flex-wrap md:flex-nowrap flex items-center justify-between mb-2">
        <p className="text-[11px] text-slate-400">{zh
          ? (view === "funnel" ? "各来源转化对比：线索 → 有效 → 签约（越靠上量越大）" : view === "roi" ? "各来源 ROI% 对比（绿=盈利 红=亏损）" : "各来源获客成本对比（每签约 1 单花费，越短越好）")
          : (view === "funnel" ? "Leads → Qualified → Signed per source" : view === "roi" ? "ROI% by source" : "Cost per signed by source")}</p>
        <div className="flex-wrap md:flex-nowrap flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          {btn("funnel", zh ? "转化对比" : "Funnel")}{btn("roi", "ROI")}{btn("cost", zh ? "获客成本" : "Cost")}
        </div>
      </div>
      {data.length === 0 ? <p className="text-xs text-slate-300 text-center py-4">{zh ? "该视图暂无可比数据" : "No data"}</p> : (
        <ResponsiveContainer width="100%" height={Math.max(140, data.length * (view === "funnel" ? 40 : 28) + 30)}>
          <BarChart data={data} layout="vertical" margin={{ top: 0, right: 40, left: 4, bottom: 0 }} barGap={2}>
            <XAxis type="number" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => view === "roi" ? `${v}%` : view === "cost" ? `$${v}` : v} />
            <YAxis type="category" dataKey="source" width={110} tick={{ fontSize: 11, fill: "#334155" }} />
            <Tooltip formatter={(v, name) => view === "roi" ? [`${v}%`, "ROI"] : view === "cost" ? [`$${Number(v).toLocaleString()}`, zh ? "获客成本" : "Cost/signed"] : [v, name]} />
            {view === "funnel" ? (<>
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="leads" name={zh ? "线索" : "Leads"} fill="#c7d2fe" radius={[0, 3, 3, 0]} barSize={9} />
              <Bar dataKey="qualified" name={zh ? "有效" : "Qualified"} fill="#7B5CFF" radius={[0, 3, 3, 0]} barSize={9} />
              <Bar dataKey="signed" name={zh ? "签约" : "Signed"} fill="#16a34a" radius={[0, 3, 3, 0]} barSize={9} />
            </>) : (
              <Bar dataKey={view === "roi" ? "roi" : "costPerSigned"} radius={[0, 4, 4, 0]} barSize={14} isAnimationActive={false}>
                {data.map((r) => <Cell key={r.source} fill={view === "roi" ? (r.roi < 0 || (roiTarget > 0 && r.roi < roiTarget) ? "#dc2626" : "#16a34a") : "#f59e0b"} />)}
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
      {noLead.length > 0 && (
        <p className="mt-1.5 text-[11px] leading-relaxed text-amber-700" data-testid={`src-nolead-${group.region}`}>
          {zh ? "已花费但暂无客户：" : "Spent with no leads yet: "}
          {noLead.map((r) => `${r.source} $${Number(r.adSpend || 0).toLocaleString()}`).join(zh ? "、" : ", ")}
          {zh ? "（活动未开始或客户未到，花费仍照实计入）" : " (spend still counted)"}
        </p>
      )}
    </div>
  );
}
